import { useEffect, useState } from "react";
import { http, money } from "@/lib/api";
import { TrendingUp, ShoppingCart, Users, Package, IndianRupee, Activity } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from "recharts";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    http.get("/admin/analytics").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-white/60">Loading…</div>;
  const k = data.kpis;

  const cards = [
    { label: "Revenue", value: money(k.revenue), icon: IndianRupee, accent: "#7f77dd" },
    { label: "Orders", value: k.orders, icon: ShoppingCart, accent: "#2a9d6f" },
    { label: "Paid orders", value: k.paid_orders, icon: TrendingUp, accent: "#ffc400" },
    { label: "Products", value: k.products, icon: Package, accent: "#7f77dd" },
    { label: "Signups", value: k.signups, icon: Users, accent: "#2a9d6f" },
    { label: "Conversion", value: `${k.conversion}%`, icon: Activity, accent: "#7f77dd" },
  ];

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-white">Dashboard</h1>
          <p className="text-white/45 text-sm mt-1">Real-time view of your Blesco store.</p>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bls-admin-card p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-white/45 font-medium">{c.label}</span>
              <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${c.accent}22` }}>
                <c.icon className="w-3.5 h-3.5" style={{ color: c.accent }} />
              </div>
            </div>
            <div className="font-display text-2xl font-bold mt-3">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="bls-admin-card p-6 lg:col-span-2">
          <h2 className="font-display font-bold text-lg">Revenue · last 14 days</h2>
          <div className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.revenue_by_day}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7f77dd" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#7f77dd" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3d" />
                <XAxis dataKey="date" stroke="#ffffff55" fontSize={11} />
                <YAxis stroke="#ffffff55" fontSize={11} />
                <Tooltip contentStyle={{ background: "#16161f", border: "1px solid #2a2a3d", borderRadius: 8, color: "white" }} />
                <Area type="monotone" dataKey="revenue" stroke="#7f77dd" fill="url(#rev)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bls-admin-card p-6">
          <h2 className="font-display font-bold text-lg">Top products</h2>
          <div className="mt-4 space-y-3">
            {data.top_products.length === 0 ? (
              <p className="text-white/45 text-sm">No orders yet.</p>
            ) : (
              data.top_products.map((p, i) => (
                <div key={p.product_id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-6 h-6 rounded-md bg-[#7f77dd]/20 text-[#7f77dd] text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="text-sm truncate">{p.name}</span>
                  </div>
                  <span className="text-xs text-white/45 font-mono">{p.units}u</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 bls-admin-card p-6">
        <h2 className="font-display font-bold text-lg">Orders by status</h2>
        <div className="h-56 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={Object.entries(data.status_counts).map(([k, v]) => ({ status: k, count: v }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3d" />
              <XAxis dataKey="status" stroke="#ffffff55" fontSize={11} />
              <YAxis stroke="#ffffff55" fontSize={11} />
              <Tooltip contentStyle={{ background: "#16161f", border: "1px solid #2a2a3d", borderRadius: 8, color: "white" }} />
              <Bar dataKey="count" fill="#7f77dd" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
